import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";
import { PageHeader, NoFarm } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Utensils } from "lucide-react";

const EMPTY = {
  name: "", group_type: "dairy", head_count: 0, avg_weight: 0, target_intake: 0,
  active_ration_id: null, milk_yield_l: 0, fat_pct: 4.0, protein_pct: 3.4, lactose_pct: 4.8,
  days_pregnant: 0, calf_birth_weight: 45, bw_change: 0, activity_factor: 1.1, is_growing: false, adg: 0,
};
const NONE = "__none__";

function Num({ label, k, form, set, unit }) {
  return (
    <div>
      <Label className="text-xs">{label}{unit ? ` (${unit})` : ""}</Label>
      <Input type="number" step="any" data-testid={`group-${k}-input`} value={form[k]}
        onChange={(e) => set(k, e.target.value)} className="mt-1" />
    </div>
  );
}

export default function AnimalGroups() {
  const { farmId, loading } = useFarm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: groups = [] } = useQuery({
    queryKey: ["animal-groups", farmId],
    queryFn: async () => (await api.get(`/farms/${farmId}/animal-groups`)).data,
    enabled: !!farmId,
  });
  const { data: rations = [] } = useQuery({
    queryKey: ["rations", farmId],
    queryFn: async () => (await api.get(`/farms/${farmId}/rations`)).data,
    enabled: !!farmId,
  });

  const rationName = (id) => rations.find((r) => r.id === id)?.name;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["animal-groups", farmId] });
    qc.invalidateQueries({ queryKey: ["dashboard", farmId] });
    qc.invalidateQueries({ queryKey: ["recs", farmId] });
  };

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? api.put(`/farms/${farmId}/animal-groups/${editing.id}`, payload)
              : api.post(`/farms/${farmId}/animal-groups`, payload),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? "Group updated" : "Group added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/farms/${farmId}/animal-groups/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...EMPTY, ...g, active_ration_id: g.active_ration_id || null }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const numKeys = ["head_count", "avg_weight", "target_intake", "milk_yield_l", "fat_pct",
      "protein_pct", "lactose_pct", "days_pregnant", "calf_birth_weight", "bw_change", "activity_factor", "adg"];
    const payload = { ...form };
    numKeys.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? 0 : Number(payload[k]); });
    payload.is_growing = !!form.is_growing;
    payload.active_ration_id = form.active_ration_id || null;
    save.mutate(payload);
  };

  if (!loading && !farmId) return <NoFarm />;

  return (
    <div>
      <PageHeader
        title="Animal Groups"
        subtitle="Group the herd, set each group's production inputs, and assign an active ration for per-group nutrient balancing."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="group-add-button" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Add group</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit" : "Add"} animal group</DialogTitle>
                <DialogDescription>Set herd details, production inputs and the active ration.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-xs">Name</Label>
                    <Input data-testid="group-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={form.group_type} onValueChange={(v) => set("group_type", v)}>
                      <SelectTrigger data-testid="group-group_type-input" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dairy">Dairy</SelectItem>
                        <SelectItem value="beef">Beef</SelectItem>
                        <SelectItem value="youngstock">Youngstock</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Num label="Head" k="head_count" form={form} set={set} />
                  <Num label="Liveweight" k="avg_weight" unit="kg" form={form} set={set} />
                  <Num label="Intake" k="target_intake" unit="kg DM/d" form={form} set={set} />
                  <Num label="Milk yield" k="milk_yield_l" unit="L/d" form={form} set={set} />
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Production & status inputs</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Num label="Milk fat" k="fat_pct" unit="%" form={form} set={set} />
                    <Num label="Milk protein" k="protein_pct" unit="%" form={form} set={set} />
                    <Num label="Lactose" k="lactose_pct" unit="%" form={form} set={set} />
                    <Num label="Days pregnant" k="days_pregnant" form={form} set={set} />
                    <Num label="Calf birth wt" k="calf_birth_weight" unit="kg" form={form} set={set} />
                    <Num label="LW change" k="bw_change" unit="kg/d" form={form} set={set} />
                    <Num label="Activity factor" k="activity_factor" form={form} set={set} />
                    <div className="flex items-end gap-2 pb-1">
                      <Checkbox checked={form.is_growing} data-testid="group-is_growing-input" onCheckedChange={(c) => set("is_growing", c)} />
                      <span className="text-sm">Growing</span>
                    </div>
                    {form.is_growing && <Num label="ADG" k="adg" unit="kg/d" form={form} set={set} />}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Active ration</Label>
                  <Select value={form.active_ration_id || NONE} onValueChange={(v) => set("active_ration_id", v === NONE ? null : v)}>
                    <SelectTrigger data-testid="group-active-ration-select" className="mt-1"><SelectValue placeholder="No ration assigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No ration assigned</SelectItem>
                      {rations.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Save a ration from the Ration Formulation page (you can assign it to a group there too).</p>
                </div>

                <DialogFooter>
                  <Button type="submit" className="rounded-full" data-testid="group-save-button" disabled={save.isPending}>{editing ? "Save" : "Add"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="overflow-x-auto">
        <Table data-testid="group-table">
          <TableHeader>
            <TableRow>
              <TableHead>Group</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Head</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead className="text-right">Intake</TableHead>
              <TableHead className="text-right">Yield</TableHead>
              <TableHead>Active ration</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No groups yet. Click Add group.</TableCell></TableRow>
            )}
            {groups.map((g, i) => (
              <TableRow key={g.id} className={i % 2 ? "bg-secondary/40" : ""} data-testid={`group-row-${g.id}`}>
                <TableCell className="font-medium">{g.name}</TableCell>
                <TableCell className="capitalize">{g.group_type}</TableCell>
                <TableCell className="text-right tabular-nums">{g.head_count}</TableCell>
                <TableCell className="text-right tabular-nums">{g.avg_weight} kg</TableCell>
                <TableCell className="text-right tabular-nums">{g.target_intake}</TableCell>
                <TableCell className="text-right tabular-nums">{g.milk_yield_l || 0} L</TableCell>
                <TableCell data-testid={`group-active-ration-${g.id}`}>
                  {g.active_ration_id ? (
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <Utensils className="h-3.5 w-3.5 text-primary" /> {rationName(g.active_ration_id) || "Assigned"}
                    </span>
                  ) : <span className="text-xs text-muted-foreground">None assigned</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" data-testid={`group-edit-${g.id}`} onClick={() => openEdit(g)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive" data-testid={`group-delete-${g.id}`}
                    onClick={() => window.confirm("Delete this group?") && del.mutate(g.id)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
